import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileText, Loader2, Users } from 'lucide-react';
import { ReportCard, ReportCardsApi } from '@/services/report-cards.api';
import { AcademicClassesApi, AcademicYearsApi } from '@/services/academic.api';
import { FeesApi } from '@/services/fees.api';
import { FEEDBACK_TIER_OPTIONS } from '@/services/co-scholastic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Field, Select } from '@/components/ui/Input';

/**
 * Report Card — Header + Attendance + Fees + Academic Performance +
 * Co-scholastic. Academic Performance (published exam results) and
 * Co-scholastic (see co-scholastic.api.ts) both render here now — real
 * backing tables exist for both, composed live by report-cards.service.ts.
 * Two modes: one student (search/preview/download) or a whole
 * class-section (bulk generate + poll).
 */
export function ReportCardPage() {
  const [tab, setTab] = useState<'student' | 'bulk'>('student');

  return (
    <>
      <PageHeader title="Report Card" description="Attendance and fee status for a student, or generate for a whole class section." />

      <div className="mb-4 flex items-center gap-1 border-b border-slate-200">
        {(
          [
            { key: 'student', label: 'Single Student' },
            { key: 'bulk', label: 'Bulk Generate' },
          ] as const
        ).map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`border-b-2 px-4 py-2 text-sm font-medium ${
              tab === t.key ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'student' ? <SingleStudentView /> : <BulkView />}
    </>
  );
}

function useYearsAndClasses() {
  const { data: yearsPage } = useQuery({
    queryKey: ['academic-years', 'all-for-report-card'],
    queryFn: () => AcademicYearsApi.list({ limit: 100, sortBy: 'start_date', sortOrder: 'desc' }),
  });
  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', 'all-for-report-card'],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }),
  });
  return { years: yearsPage?.items ?? [], classes: classesPage?.items ?? [] };
}

// ───── Single student ───────────────────────────────────────────────────────
function SingleStudentView() {
  const { years, classes } = useYearsAndClasses();
  const [academicYearId, setAcademicYearId] = useState('');
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [studentId, setStudentId] = useState('');
  const [pdfRequested, setPdfRequested] = useState(false);

  useEffect(() => {
    if (years.length && !academicYearId) setAcademicYearId(years.find((y: any) => y.isCurrent)?.id ?? years[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [years]);

  const sections = classes.find((c) => c.id === classId)?.sections ?? [];

  const { data: students } = useQuery({
    queryKey: ['report-card', 'students-picker', classId, sectionId],
    queryFn: () => FeesApi.studentsPicker({ classId: classId || undefined, sectionId: sectionId || undefined }),
    enabled: !!classId,
  });

  const { data: card, isFetching, isError, refetch } = useQuery({
    queryKey: ['report-card', studentId, academicYearId],
    queryFn: () => ReportCardsApi.get(studentId, academicYearId || undefined),
    enabled: !!studentId,
  });

  const { data: pdfStatus } = useQuery({
    queryKey: ['report-card-pdf', studentId, academicYearId],
    queryFn: () => ReportCardsApi.pdf(studentId, academicYearId || undefined),
    enabled: !!studentId && pdfRequested,
    refetchInterval: (q) => (q.state.data?.status === 'generating' ? 2000 : false),
  });

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
      <div className="card space-y-3 p-5 lg:col-span-1">
        <Field label="Academic Year">
          <Select value={academicYearId} onChange={(e) => setAcademicYearId(e.target.value)}>
            {years.map((y: any) => (
              <option key={y.id} value={y.id}>
                {y.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Class">
          <Select
            value={classId}
            onChange={(e) => {
              setClassId(e.target.value);
              setSectionId('');
              setStudentId('');
            }}
          >
            <option value="">Select class</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Section">
          <Select
            value={sectionId}
            onChange={(e) => {
              setSectionId(e.target.value);
              setStudentId('');
            }}
            disabled={!classId}
          >
            <option value="">All sections</option>
            {sections.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Student">
          <Select
            value={studentId}
            onChange={(e) => {
              setStudentId(e.target.value);
              setPdfRequested(false);
            }}
            disabled={!classId}
          >
            <option value="">Select student</option>
            {(students ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.admissionNo})
              </option>
            ))}
          </Select>
        </Field>
        {studentId && (
          <button className="btn-primary w-full" onClick={() => setPdfRequested(true)} disabled={pdfStatus?.status === 'generating'}>
            {pdfStatus?.status === 'generating' ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Download className="mr-1.5 h-4 w-4" />
            )}
            {pdfStatus?.status === 'generating' ? 'Generating…' : 'Download PDF'}
          </button>
        )}
        {pdfStatus?.status === 'ready' && pdfStatus.pdfUrl && (
          <a href={pdfStatus.pdfUrl} target="_blank" rel="noreferrer" className="block text-center text-sm font-medium text-brand-600 hover:underline">
            Open PDF →
          </a>
        )}
      </div>

      <div className="lg:col-span-2">
        {!studentId ? (
          <div className="card p-8 text-center text-slate-400">Select a student to preview their report card.</div>
        ) : isError ? (
          <div className="card space-y-2 p-8 text-center">
            <p className="text-sm text-red-600">Could not load this report card.</p>
            <button className="btn-secondary" onClick={() => refetch()}>
              Retry
            </button>
          </div>
        ) : isFetching || !card ? (
          <div className="card p-8 text-center text-slate-400">Loading…</div>
        ) : (
          <ReportCardPreview card={card} />
        )}
      </div>
    </div>
  );
}

const TIER_LABEL = Object.fromEntries(FEEDBACK_TIER_OPTIONS.map((o) => [o.value, o.label]));
const TIER_TONE: Record<string, 'green' | 'amber' | 'red' | 'slate'> = {
  excellent: 'green',
  good: 'green',
  basic: 'amber',
  need_improvement: 'red',
};

function ReportCardPreview({ card }: { card: ReportCard }) {
  const { header, attendance, fees, academicPerformance, coScholastic } = card;
  const bandTone = (band: 'green' | 'amber' | 'red' | null): 'green' | 'amber' | 'red' | 'slate' => band ?? 'slate';

  return (
    <div className="space-y-5">
      <div className="card p-5">
        <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-700">
          <FileText className="h-4 w-4" /> {header.studentName}
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm text-slate-600 sm:grid-cols-4">
          <div>
            <div className="text-xs uppercase text-slate-400">Admission No</div>
            {header.admissionNo}
          </div>
          <div>
            <div className="text-xs uppercase text-slate-400">Class · Section</div>
            {header.className} · {header.sectionName}
          </div>
          <div>
            <div className="text-xs uppercase text-slate-400">Roll No</div>
            {header.rollNumber ?? '—'}
          </div>
          <div>
            <div className="text-xs uppercase text-slate-400">Academic Year</div>
            {header.academicYearName}
          </div>
        </div>
      </div>

      {attendance ? (
        <div className="card p-5">
          <h3 className="mb-3 text-sm font-semibold text-slate-700">Attendance</h3>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase text-slate-400">Present</span>
              <Badge tone="green">{attendance.present}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase text-slate-400">Absent</span>
              <Badge tone="red">{attendance.absent}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase text-slate-400">Late</span>
              <Badge tone="amber">{attendance.late}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase text-slate-400">Half Day</span>
              <Badge tone="slate">{attendance.halfDay}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase text-slate-400">Holiday</span>
              <Badge tone="slate">{attendance.holiday}</Badge>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs uppercase text-slate-400">Attendance</span>
              <Badge tone={bandTone(attendance.band)}>{attendance.percentage != null ? `${attendance.percentage}%` : 'N/A'}</Badge>
            </div>
          </div>
        </div>
      ) : (
        <div className="card p-5 text-sm italic text-slate-400">No attendance records for this student/year.</div>
      )}

      {fees ? (
        <div className="card p-5">
          <h3 className="mb-3 text-sm font-semibold text-slate-700">Fees</h3>
          <div className="mb-3 flex flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase text-slate-400">Demanded</div>
              <div className="text-lg font-semibold text-slate-900">₹{fees.demanded.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-xs uppercase text-slate-400">Paid</div>
              <div className="text-lg font-semibold text-slate-900">₹{fees.paid.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-xs uppercase text-slate-400">Pending</div>
              <Badge tone={fees.pending > 0 ? 'slate' : 'green'}>₹{fees.pending.toFixed(2)}</Badge>
            </div>
          </div>
          {fees.rows.length > 0 && (
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-slate-400">
                  <th className="py-1">Fee</th>
                  <th className="py-1 text-right">Amount</th>
                  <th className="py-1 text-right">Discount</th>
                  <th className="py-1 text-right">Paid</th>
                  <th className="py-1 text-right">Pending</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {fees.rows.map((r, i) => (
                  <tr key={i}>
                    <td className="py-1.5">{r.feeStructureName}</td>
                    <td className="py-1.5 text-right tabular-nums">₹{r.amount.toFixed(2)}</td>
                    <td className="py-1.5 text-right tabular-nums">₹{r.discount.toFixed(2)}</td>
                    <td className="py-1.5 text-right tabular-nums">₹{r.paid.toFixed(2)}</td>
                    <td className="py-1.5 text-right tabular-nums">₹{r.pending.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div className="card p-5 text-sm italic text-slate-400">No fee records for this student/year.</div>
      )}

      {academicPerformance ? (
        <div className="card p-5">
          <h3 className="mb-3 text-sm font-semibold text-slate-700">Academic Performance</h3>
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-400">
                <th className="py-1">Exam</th>
                <th className="py-1 text-right">Marks</th>
                <th className="py-1 text-right">%</th>
                <th className="py-1 text-right">Grade</th>
                <th className="py-1 text-right">Result</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {academicPerformance.map((r) => (
                <tr key={r.examId}>
                  <td className="py-1.5">{r.examName}</td>
                  <td className="py-1.5 text-right tabular-nums">
                    {r.totalObtained}/{r.totalMax}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{r.overallPercent}%</td>
                  <td className="py-1.5 text-right">{r.cumulativeGrade}</td>
                  <td className="py-1.5 text-right">
                    <Badge tone={r.finalPassStatus ? 'green' : 'red'}>{r.finalPassStatus ? 'Pass' : 'Fail'}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card p-5 text-sm italic text-slate-400">No published exam results for this student/year.</div>
      )}

      {coScholastic ? (
        <div className="card p-5">
          <h3 className="mb-3 text-sm font-semibold text-slate-700">Co-scholastic Assessment</h3>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {coScholastic.map((r) => (
              <div key={r.categoryId} className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2">
                <div>
                  <div className="text-sm text-slate-700">{r.categoryName}</div>
                  <div className="text-xs text-slate-400">{r.term}</div>
                </div>
                <Badge tone={TIER_TONE[r.feedbackTier] ?? 'slate'}>{TIER_LABEL[r.feedbackTier] ?? r.feedbackTier}</Badge>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="card p-5 text-sm italic text-slate-400">No co-scholastic assessment for this student/year.</div>
      )}
    </div>
  );
}

// ───── Bulk generate ────────────────────────────────────────────────────────
function BulkView() {
  const qc = useQueryClient();
  const { years, classes } = useYearsAndClasses();
  const [academicYearId, setAcademicYearId] = useState('');
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');

  useEffect(() => {
    if (years.length && !academicYearId) setAcademicYearId(years.find((y: any) => y.isCurrent)?.id ?? years[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [years]);

  const sections = classes.find((c) => c.id === classId)?.sections ?? [];

  const generate = useMutation({
    mutationFn: () => ReportCardsApi.bulk(sectionId, academicYearId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['report-card-bulk-status', sectionId, academicYearId] }),
  });

  // Poll purely off the query's own data — no external state to fall out
  // of sync with. Keeps polling whenever any row lacks a pdfUrl, whether
  // that's from a generate just triggered in this session or PDFs still
  // finishing from an earlier one (e.g. the page was reloaded mid-batch).
  const { data: rows, isFetching } = useQuery({
    queryKey: ['report-card-bulk-status', sectionId, academicYearId],
    queryFn: () => ReportCardsApi.bulkStatus(sectionId, academicYearId),
    enabled: !!sectionId && !!academicYearId,
    refetchInterval: (q) => {
      const items = q.state.data;
      if (!items || items.length === 0) return false;
      return items.some((i) => !i.pdfUrl) ? 2500 : false;
    },
  });

  const pending = (rows ?? []).filter((r) => !r.pdfUrl).length;

  return (
    <>
      <div className="card mb-5 flex flex-wrap items-end gap-3 p-5">
        <Field label="Academic Year">
          <Select value={academicYearId} onChange={(e) => setAcademicYearId(e.target.value)}>
            {years.map((y: any) => (
              <option key={y.id} value={y.id}>
                {y.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Class">
          <Select
            value={classId}
            onChange={(e) => {
              setClassId(e.target.value);
              setSectionId('');
            }}
          >
            <option value="">Select class</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Section">
          <Select value={sectionId} onChange={(e) => setSectionId(e.target.value)} disabled={!classId}>
            <option value="">Select section</option>
            {sections.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </Field>
        <button className="btn-primary" onClick={() => generate.mutate()} disabled={!sectionId || !academicYearId || generate.isPending}>
          {generate.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Users className="mr-1.5 h-4 w-4" />}
          Generate for section
        </button>
      </div>

      {generate.error && <div className="mb-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">Could not generate — please try again.</div>}

      {!sectionId ? (
        <div className="card p-8 text-center text-slate-400">Select a class and section, then Generate.</div>
      ) : isFetching && !rows ? (
        <div className="card p-8 text-center text-slate-400">Loading…</div>
      ) : !rows || rows.length === 0 ? (
        <div className="card p-8 text-center text-slate-500">No active students in this section for this academic year.</div>
      ) : (
        <>
          {pending > 0 && (
            <div className="mb-3 flex items-center gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
              <Loader2 className="h-4 w-4 animate-spin" />
              Generating {pending} PDF{pending > 1 ? 's' : ''} in the background…
            </div>
          )}
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Roll</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Student</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Report card</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.studentId} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-500">{r.rollNumber ?? '—'}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">{r.studentName}</div>
                      <div className="text-xs text-slate-400">{r.admissionNo}</div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {r.pdfUrl ? (
                        <a href={r.pdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm font-medium text-brand-600 hover:underline">
                          <Download className="h-3.5 w-3.5" /> PDF
                        </a>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" /> pending…
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
}
